import canva from '@/assets/tools/canva.svg'
import facebook from '@/assets/tools/facebook.svg'
import googleAnalytics from '@/assets/tools/google_analytics.svg'
import googleMaps from '@/assets/tools/google_maps.svg'
import googleSearchConsole from '@/assets/tools/google_search_console.svg'
import googleads from '@/assets/tools/googleads.svg'
import googledocs from '@/assets/tools/googledocs.svg'
import googledrive from '@/assets/tools/googledrive.svg'
import instagram from '@/assets/tools/instagram.svg'
import linkedin from '@/assets/tools/linkedin.svg'
import linkedinAds from '@/assets/tools/linkedin_ads.svg'
import metaads from '@/assets/tools/metaads.svg'
import reddit from '@/assets/tools/reddit.svg'
import redditAds from '@/assets/tools/reddit_ads.svg'
import sentry from '@/assets/tools/sentry.svg'
import slack from '@/assets/tools/slack.svg'
import tiktok from '@/assets/tools/tiktok.svg'
import tiktokAds from '@/assets/tools/tiktok_ads.svg'
import twitter from '@/assets/tools/twitter.svg'
import youtube from '@/assets/tools/youtube.svg'
import { cn } from '@/lib/utils'

/**
 * Logos for the outside apps the AI team uses (Composio toolkits).
 *
 * To add one: drop the SVG in src/assets/tools/<toolkit>.svg, import it above
 * and add a row here. `toolkit` must match the Composio slug in
 * backend/super_agents/accounts.py CATALOG.
 */
export interface ToolIcon {
  toolkit: string
  name: string
  src: string
}

export const TOOL_ICONS: ToolIcon[] = [
  { toolkit: 'instagram', name: 'Instagram', src: instagram },
  { toolkit: 'facebook', name: 'Facebook', src: facebook },
  { toolkit: 'linkedin', name: 'LinkedIn', src: linkedin },
  { toolkit: 'twitter', name: 'X (Twitter)', src: twitter },
  { toolkit: 'tiktok', name: 'TikTok', src: tiktok },
  { toolkit: 'youtube', name: 'YouTube', src: youtube },
  { toolkit: 'reddit', name: 'Reddit', src: reddit },
  { toolkit: 'googleads', name: 'Google Ads', src: googleads },
  { toolkit: 'metaads', name: 'Meta Ads', src: metaads },
  { toolkit: 'tiktok_ads', name: 'TikTok Ads', src: tiktokAds },
  { toolkit: 'linkedin_ads', name: 'LinkedIn Ads', src: linkedinAds },
  { toolkit: 'reddit_ads', name: 'Reddit Ads', src: redditAds },
  { toolkit: 'canva', name: 'Canva', src: canva },
  { toolkit: 'slack', name: 'Slack', src: slack },
  { toolkit: 'google_maps', name: 'Google Maps', src: googleMaps },
  { toolkit: 'googledrive', name: 'Google Drive', src: googledrive },
  { toolkit: 'googledocs', name: 'Google Docs', src: googledocs },
  {
    toolkit: 'google_analytics',
    name: 'Google Analytics',
    src: googleAnalytics
  },
  {
    toolkit: 'google_search_console',
    name: 'Google Search Console',
    src: googleSearchConsole
  },
  { toolkit: 'sentry', name: 'Sentry', src: sentry }
]

const BY_TOOLKIT = new Map(TOOL_ICONS.map((t) => [t.toolkit, t]))
// Composio action slugs start with the toolkit in capitals, e.g. INSTAGRAM_CREATE_POST.
const PREFIXES = [...TOOL_ICONS].sort(
  (a, b) => b.toolkit.length - a.toolkit.length
)

export function toolIcon(
  toolkitOrAction?: string | null
): ToolIcon | undefined {
  if (!toolkitOrAction) return undefined
  const key = toolkitOrAction.toLowerCase()
  return (
    BY_TOOLKIT.get(key) ?? PREFIXES.find((t) => key.startsWith(`${t.toolkit}_`))
  )
}

const PX = { xs: 16, sm: 20, md: 28, lg: 40 } as const

/** A tool's logo as the brand ships it: no background, no ring, just the mark at a consistent size. */
export function ToolLogo({
  toolkit,
  size = 'sm',
  className
}: {
  toolkit?: string | null
  size?: keyof typeof PX
  className?: string
}) {
  const icon = toolIcon(toolkit)
  if (!icon) return null
  const px = PX[size]
  return (
    <img
      src={icon.src}
      alt={icon.name}
      title={icon.name}
      width={px}
      height={px}
      style={{ width: px, height: px }}
      className={cn(
        'inline-block shrink-0 select-none object-contain',
        className
      )}
      draggable={false}
      decoding="async"
    />
  )
}
